import getConfig from 'next/config'
import { callAPI } from './callAPI'

const {
  publicRuntimeConfig: { API_BASE_PATH },
} = getConfig()

// helper to convert string to allow string literals to be inserted into regex functions
const regexifyStringLiteral = (str: string) => {
  const escapedSet = ['[', ']', '-', '|']
  let newStr = ''
  for (let idx = 0; idx < str.length; idx += 1) {
    if (escapedSet.includes(str[idx])) {
      newStr += `\\${str[idx]}`
    } else {
      newStr += str[idx]
    }
  }
  return newStr
}

const getUpdatedURL = (
  buttonURL: string,
  insertQueryParam: string,
  decodedQueryParamTarget: string,
  appendToRParam: boolean,
  insertQueryParamLocation: string
) => {
  let updatedURL = ''
  const contentfulQueryParams = insertQueryParam && insertQueryParam.replace(/\s/g, '').split(',')
  const filteredQueryParams = []
  let urlQueryStringNewParamString = ''
  const urlParams = new URLSearchParams(window.location.search)
  let unmodifiedQueryValue = ''

  // gets list of entries from contentful field that are in current url to append to button url
  contentfulQueryParams &&
    contentfulQueryParams.forEach((param) => {
      if (urlParams.has(param)) {
        filteredQueryParams.push(param)
      }
    })

  // build updated subsection of query string with filtered query params
  if (filteredQueryParams.length > 0) {
    filteredQueryParams.forEach((param) => {
      urlQueryStringNewParamString += `${param}=${urlParams.get(param)}&`
    })
  }

  if (decodedQueryParamTarget) {
    if (buttonURL.includes(`${decodedQueryParamTarget}=`)) {
      unmodifiedQueryValue = buttonURL.split(`${decodedQueryParamTarget}=`).pop()

      if (unmodifiedQueryValue.indexOf('&') !== -1) {
        unmodifiedQueryValue = unmodifiedQueryValue.substring(0, unmodifiedQueryValue.indexOf('&'))
      }

      unmodifiedQueryValue = decodeURIComponent(unmodifiedQueryValue)
    }
  }

  if (unmodifiedQueryValue) {
    const url = new URL(unmodifiedQueryValue)
    const params = new URLSearchParams(url.search)
    let rParam = params.get('r')

    // append R query param into unmodifiedQueryValue
    if (appendToRParam && rParam && filteredQueryParams.length > 0) {
      const newQueryParams = urlQueryStringNewParamString.slice(0, -1)

      if (rParam.slice(-1) === '?' || rParam.slice(-1) === '&') {
        rParam += newQueryParams
      } else {
        rParam = `${rParam}?${newQueryParams}`
      }

      params.set('r', encodeURIComponent(rParam))

      // regex assumes that there is a query param behind 'r'
      const regex = /(r=)([^&]*)/
      unmodifiedQueryValue = unmodifiedQueryValue.replace(regex, `$1${params.get('r')}`)
    }
  }

  // if insertion location isn't specified or query string is not included in URL, append to start
  if (
    !insertQueryParamLocation ||
    (!buttonURL.includes(insertQueryParamLocation) && !unmodifiedQueryValue.includes(insertQueryParamLocation))
  ) {
    const questionMarkIndex = unmodifiedQueryValue ? unmodifiedQueryValue.lastIndexOf('?') : buttonURL.indexOf('?')
    if (questionMarkIndex === -1) {
      if (unmodifiedQueryValue) return buttonURL
      updatedURL = `${buttonURL}?${urlQueryStringNewParamString}`
    } else {
      updatedURL = unmodifiedQueryValue
        ? unmodifiedQueryValue.slice(0, questionMarkIndex + 1) +
          urlQueryStringNewParamString +
          unmodifiedQueryValue.slice(questionMarkIndex + 1, buttonURL.length)
        : buttonURL.slice(0, questionMarkIndex + 1) +
          urlQueryStringNewParamString +
          buttonURL.slice(questionMarkIndex + 1, buttonURL.length)
    }
  } else {
    // if query string exists, use regex to find its insertion location
    const filteredQueryParamLocation = regexifyStringLiteral(insertQueryParamLocation)
    let stringInsideSquareBrackets = 'nomatch'
    const squareBracketExists = /\[(.*?)\]/.exec(insertQueryParamLocation)
    if (squareBracketExists) {
      stringInsideSquareBrackets = regexifyStringLiteral(squareBracketExists[1])
    }
    const regexPattern = RegExp(
      `(${filteredQueryParamLocation}=[^&]+&)|([a-zA-Z0-9_]+\\[${stringInsideSquareBrackets}\\]&)`,
      'g'
    )

    const regexPatternEnd = RegExp(
      `(${filteredQueryParamLocation}=[^&]+|([a-zA-Z0-9_]+\\[${stringInsideSquareBrackets}\\]))`,
      'g'
    )

    const regexMatchEnd = regexPatternEnd.exec(unmodifiedQueryValue)

    const regexMatch = regexPattern.exec(unmodifiedQueryValue)

    if (regexMatch) {
      updatedURL =
        unmodifiedQueryValue.slice(0, regexMatch.index + regexMatch[0].length) +
        urlQueryStringNewParamString +
        unmodifiedQueryValue.slice(regexMatch.index + regexMatch[0].length, buttonURL.length)
    }

    if (regexMatchEnd && !regexMatch) {
      updatedURL = `${unmodifiedQueryValue}&${urlQueryStringNewParamString}`
    }
  }

  // removes trailing ampersand if exists
  if (updatedURL[updatedURL.length - 1] === '&') {
    updatedURL = updatedURL.slice(0, updatedURL.length - 1)
  }

  // re-encodes new updated URL from encoded query param into full CTA URL and returns result
  if (decodedQueryParamTarget) {
    const encodedUpdatedURL = encodeURIComponent(updatedURL)

    const replace = `(${decodedQueryParamTarget}=[^&?]+)`
    const re = new RegExp(replace, 'g')

    const decodedQueryParamMatches = buttonURL.match(re)

    if (!decodedQueryParamMatches) {
      return buttonURL
    }

    return buttonURL.replace(
      decodedQueryParamMatches[decodedQueryParamMatches.length - 1],
      `${decodedQueryParamTarget}=${encodedUpdatedURL}`
    )
  }
}

const callTapAPI = async (
  url: string,
  productOfferingId: string,
  setShowSpinner: (data) => void,
  setHasApiError: (data) => void
) => {
  setShowSpinner(true)
  const urlParams = new URLSearchParams(window.location.search)
  const PRN = urlParams.get('PRN')
  const CID = urlParams.get('CID')
  if (CID && PRN && productOfferingId) {
    try {
      const tapAPIResponse = await callAPI(
        `${API_BASE_PATH}api/tap?CID=${CID}&PRN=${PRN}&PID=${productOfferingId}`,
        setHasApiError
      )
      if (tapAPIResponse) {
        setShowSpinner(false)

        if (tapAPIResponse?.status && tapAPIResponse?.status !== 'failed') {
          return window.location.replace(url)
        }
      }
    } catch (e) {
      setHasApiError(true)
    }
  }

  setHasApiError(true)
}

export { getUpdatedURL, callTapAPI }
